import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ArrowRightIcon, CheckCircle2Icon, MapPinIcon } from 'lucide-react'

import { alpha, useTheme, ButtonBase, LinearProgress, Stack, Typography } from '@mui/material'

import { Modal } from '@repo/ui'
// Direct subpath imports, not the `components` barrel: that barrel re-exports
// this folder, so importing through it would be a cycle.
import { Search } from 'components/input/Search'

import { useProductTour } from './ProductTourProvider'
import { TourRow } from './TourRow'
import type { TourProgress } from 'services/tourProgressApi'
import { TOUR_CATEGORY_ORDER, TOURS, getOnPageTours, type TourDef } from './tours'

/** Exported so listeners and tests share one literal rather than re-typing it. */
export const TOURS_DIALOG_OPEN_EVENT = 'vera:open-tours-dialog'

/**
 * Open the globally-mounted tour library from anywhere. Safe inside an event
 * handler; a no-op if called before the window exists.
 *
 * Mirrors `openKeyboardShortcutsDialog` deliberately, including the reasoning: a
 * provider + `useContext` would put a React dependency on every would-be caller and
 * re-render its subtree to carry a boolean that matters once a session. There are
 * now three callers in different trees — the sidebar Help menu, the ⌘K palette's
 * footer, and its "Browse all tours" row — which is exactly when local dialog state
 * in one of them stops being viable.
 */
export const openToursDialog = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(TOURS_DIALOG_OPEN_EVENT))
}

/**
 * The tour library — every tour in the registry, startable on demand.
 *
 * Mounted ONCE in `Layout`, like `KeyboardShortcutsDialog`; opened by
 * `openToursDialog()`. It used to be rendered by the Help popover with local
 * `toursOpen` state, which that file's own comment argued against for the
 * shortcuts dialog: "a menu row should not own the lifecycle of a global surface".
 *
 * Three affordances, in the order they earn their place:
 *   1. "On this page" — tours whose scope anchor currently resolves in the DOM
 *      float to the top. This is the whole reason a global Help button can be
 *      route-aware without a route table: the same `data-tour` attributes the
 *      engine spotlights double as a presence check. Deleting a page's anchors
 *      removes it from this list automatically, and the build guard means they
 *      can't be deleted silently.
 *   2. Search — over label, description and category.
 *   3. Category headers — so the list still scans once there are more than a few.
 */
export const ToursDialog = () => {
  const { startTour, tourProgress } = useProductTour()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [onPageIds, setOnPageIds] = useState<Set<string>>(new Set())

  const onClose = useCallback(() => setOpen(false), [])

  useEffect(() => {
    const handleOpen = () => setOpen(true)
    window.addEventListener(TOURS_DIALOG_OPEN_EVENT, handleOpen)
    return () => window.removeEventListener(TOURS_DIALOG_OPEN_EVENT, handleOpen)
  }, [])

  // Recompute each time the dialog opens — which tours are on-page depends on
  // the current route and, for anchors inside tabs/drawers, on what is expanded.
  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    // Shared with the Help popover's "Tours on this page" — one presence check,
    // so the two surfaces can never disagree about which tours are relevant here.
    setOnPageIds(new Set(getOnPageTours().map(t => t.id)))
  }, [open])

  const launch = (id: string) => {
    // Close first: the tour needs to measure anchors on the page underneath, and
    // the dialog's backdrop would otherwise swallow the spotlight.
    onClose()
    startTour(id)
  }

  const q = query.trim().toLowerCase()

  const matches = useMemo(
    () => (t: TourDef) =>
      !q ||
      t.label.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.category.toLowerCase().includes(q),
    [q]
  )

  // The on-page section is suppressed while searching: a search is global by
  // intent, so a route-scoped shortcut in the middle of one is just noise.
  const onPageTours = useMemo(
    () => (q ? [] : TOURS.filter(t => onPageIds.has(t.id))),
    [q, onPageIds]
  )

  // On-page tours are deduped out of the category groups when not searching, so
  // each tour appears exactly once.
  const grouped = useMemo(
    () =>
      TOUR_CATEGORY_ORDER.map(category => ({
        category,
        tours: TOURS.filter(t => t.category === category && matches(t) && (q || !onPageIds.has(t.id))),
      })).filter(g => g.tours.length > 0),
    [q, onPageIds, matches]
  )

  const noResults = onPageTours.length === 0 && grouped.length === 0

  return (
    <Modal title="Product tours" open={open} onClose={onClose}>
      <Stack spacing={4}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Short interactive walkthroughs of the platform. Pick one to start it here.
        </Typography>

        <Search value={query} onChange={setQuery} placeholder="Search tours" fullWidth debounce={false} />

        {/* `mt: -2` shaves 8px off the outer Stack's 16px gap above THIS element only —
            between-category spacing stays at the full `spacing={4}` below (unaffected,
            since that's this Stack's OWN spacing, not the outer one). Needed because the
            gap here sits directly above the first (sticky) category header, which
            already carries its own vertical padding — the outer 16px on top of that read
            as a noticeably larger gap than the 16px between rows further down. */}
        <Stack spacing={4} sx={{ maxHeight: '52vh', overflowY: 'auto', mt: -2 }}>
          {onPageTours.length > 0 && (
            <Section
              label="On this page"
              icon={<MapPinIcon strokeWidth={1} size={12} />}
            >
              {onPageTours.map(t => (
                <TourRow key={t.id} tour={t} onClick={() => launch(t.id)} progress={tourProgress(t.id)} />
              ))}
            </Section>
          )}

          {grouped.map(g => (
            <Section key={g.category} label={g.category}>
              {g.tours.map(t => (
                <TourRow key={t.id} tour={t} onClick={() => launch(t.id)} progress={tourProgress(t.id)} />
              ))}
            </Section>
          ))}

          {noResults && (
            <Typography variant="body3" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              {q ? `No tours match "${query}".` : 'No tours are available yet.'}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}

const Section = ({
  label,
  icon,
  children,
}: {
  label: string
  icon?: React.ReactNode
  children: React.ReactNode
}) => (
  <Stack spacing={2}>
    {/* Sticky within the dialog's scrollable list (the `maxHeight`/`overflowY: auto`
        Stack in ToursDialog above) — as you scroll through a category's rows, its
        header stays pinned just below the search box instead of scrolling away,
        then the next category's header takes over once this one's rows pass. Needs
        its own opaque background (matching Modal's `background.paper`) so rows
        scrolling underneath don't show through. */}
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        color: 'text.secondary',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
        // Modal's content padding is `p: 4` (packages/ui/.../Modal.tsx) — matched here,
        // not guessed, so the sticky bar's edges land exactly on the dialog's own edges.
        mx: -4,
        px: 4,
        py: 1,
      }}
    >
      {icon}
      <Typography variant="body4" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
    </Stack>
    {children}
  </Stack>
)

